import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useService } from '@/contexts/ServiceContext';
import { Button } from '@/components/ui/Button';
import { useQueueEstimation } from '@/hooks/useQueueEstimation';
import { takeTicket, takeTicketWithCompanions, requestOvertimeTicket } from '@/services/queueService';
import { calculateAutoAssignedBarber } from '@/services/barberAssignmentService';
import { toast } from 'sonner';
import type { Service, Barber, Branch } from '@/types';
import { Users, Info, Baby, PersonStanding, ArrowLeft } from 'lucide-react';
import { OutOfScheduleModal } from './OutOfScheduleModal';
import CompanionSelector, { Companion } from './CompanionSelector';
import { BookingSummary } from './BookingSummary';
import { QueueEstimationCard } from './QueueEstimationCard';
import { useAvailableBarbers } from '@/hooks/useAvailableBarbers';
import { GuestNameModal } from '@/components/queue/GuestNameModal';

interface BookingConfirmationProps {
  branch: Branch;
  service: Service;
  barber: Barber;
  onBack: () => void;
  onChangeBarber?: (barber: Barber) => void;
}

export function BookingConfirmation({
  branch,
  service,
  barber,
  onBack,
  onChangeBarber,
}: BookingConfirmationProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, customClaims, firebaseUser } = useAuth();
  const [isConfirming, setIsConfirming] = useState(false);
  const [showGuestNameModal, setShowGuestNameModal] = useState(false);
  const [guestNameSaved, setGuestNameSaved] = useState(false);

  // Check if user is a guest without a proper name
  const isGuestWithoutName = customClaims?.role === 'guest' &&
    (!user?.displayName || user.displayName === 'Invitado' || user.displayName === 'Guest');
  const [preAssignedBarber, setPreAssignedBarber] = useState<Barber | null>(null);
  const [loadingPreAssignment, setLoadingPreAssignment] = useState(false);
  const [showOutOfScheduleModal, setShowOutOfScheduleModal] = useState(false);
  const [isRequestingOvertime, setIsRequestingOvertime] = useState(false);
  const [companions, setCompanions] = useState<Companion[]>([]);
  const [hasCompanionsSelected, setHasCompanionsSelected] = useState(false);

  // Get available barbers for companion selection
  const { availableBarbers } = useAvailableBarbers(branch.branchId);

  // Get all services from context (same as ServiceSelection uses)
  const { services: contextServices } = useService();

  // Filter active services, fallback to current service if none
  const availableServices = useMemo(() => {
    const activeServices = contextServices?.filter(s => s.isActive) || [];
    return activeServices.length > 0 ? activeServices : [service];
  }, [contextServices, service]);

  // Get the actual barber ID - handle "any" case
  const actualBarberId = barber.barberId || (barber as any).id || null;
  const isAnyBarber = actualBarberId === 'any';

  // Calculate which barber will be auto-assigned if "any" is selected
  useEffect(() => {
    if (isAnyBarber && branch.branchId) {
      setLoadingPreAssignment(true);
      calculateAutoAssignedBarber(branch.branchId)
        .then(assignedBarber => {
          setPreAssignedBarber(assignedBarber);
        })
        .catch(error => {
          console.error('❌ Error calculating pre-assigned barber:', error);
        })
        .finally(() => {
          setLoadingPreAssignment(false);
        });
    }
  }, [isAnyBarber, branch.branchId]);

  // Use pre-assigned barber for queue estimation if "any" is selected
  const barberForEstimation = isAnyBarber && preAssignedBarber ? preAssignedBarber : barber;
  const barberIdForEstimation = isAnyBarber && preAssignedBarber ? preAssignedBarber.barberId : actualBarberId;

  // Calculate queue estimation
  const { estimation, loading, isOutsideWorkingHours, workingHoursWarning } =
    useQueueEstimation({
      barberId: barberIdForEstimation,
      branchId: branch.branchId,
      serviceDuration: service.duration,
      barber: barberIdForEstimation !== 'any' ? barberForEstimation : null,
    });

  // Calculate overtime schedule data
  const overtimeData = useMemo(() => {
    if (!barberForEstimation?.schedule || isAnyBarber) {
      return null;
    }

    const now = new Date();
    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = daysOfWeek[now.getDay()] as keyof typeof barberForEstimation.schedule;
    const daySchedule = barberForEstimation.schedule[today];

    if (!daySchedule?.isWorking || !daySchedule?.workEnd) {
      return null;
    }

    const barberWorkEnd = daySchedule.workEnd;
    const [endHour, endMinute] = barberWorkEnd.split(':').map(Number);
    const workEndTime = new Date();
    workEndTime.setHours(endHour, endMinute, 0, 0);

    // Calculate when service would end
    const waitMinutes = estimation?.estimatedWaitMinutes || 0;
    const serviceEndTime = new Date(now.getTime() + (waitMinutes + service.duration) * 60000);

    const estimatedServiceEnd = serviceEndTime.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });

    // Calculate overtime minutes
    const overtimeMinutes = Math.max(0, Math.ceil((serviceEndTime.getTime() - workEndTime.getTime()) / 60000));

    return {
      barberWorkEnd,
      estimatedServiceEnd,
      overtimeMinutes,
    };
  }, [barberForEstimation, estimation, service.duration, isAnyBarber]);

  // Handle request for overtime ticket
  const handleRequestOvertime = async () => {
    if (!user || !overtimeData) {
      toast.error(t('booking.confirmation.errors.notLoggedIn'));
      return;
    }

    setIsRequestingOvertime(true);
    const toastId = toast.loading(t('booking.outOfSchedule.sendingRequest'));

    try {
      const result = await requestOvertimeTicket({
        branchId: branch.branchId,
        serviceId: service.serviceId,
        barberId: actualBarberId,
        barberWorkEnd: overtimeData.barberWorkEnd,
        estimatedServiceEnd: overtimeData.estimatedServiceEnd,
        overtimeMinutes: overtimeData.overtimeMinutes,
      });

      toast.dismiss(toastId);

      if (result.success) {
        toast.success(t('booking.outOfSchedule.requestSent'), {
          description: t('booking.outOfSchedule.waitingForBarber'),
        });

        // Navigate to waiting page
        setShowOutOfScheduleModal(false);
        navigate('/overtime-waiting', {
          state: {
            queueId: result.queueId,
            barberId: actualBarberId,
            barberName: barber.displayName,
          }
        });
      } else {
        toast.error(result.message || t('booking.outOfSchedule.requestFailed'));
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(
        error instanceof Error
          ? error.message
          : t('booking.outOfSchedule.requestFailed')
      );
    } finally {
      setIsRequestingOvertime(false);
    }
  };

  // Handle selecting an alternative barber
  const handleSelectAlternativeBarber = (altBarber: Barber) => {
    setShowOutOfScheduleModal(false);
    if (onChangeBarber) {
      onChangeBarber(altBarber);
    } else {
      toast.info(t('booking.outOfSchedule.selectedBarber', { name: altBarber.displayName }));
    }
  };

  const handleConfirm = async () => {
    // If guest without name, show modal first
    if (isGuestWithoutName && !guestNameSaved) {
      setShowGuestNameModal(true);
      return;
    }

    // If outside working hours, show the modal first
    if (isOutsideWorkingHours && !isAnyBarber && overtimeData) {
      setShowOutOfScheduleModal(true);
      return;
    }

    // Block confirmation if "with companions" is selected but no valid companions added
    if (hasCompanionsSelected) {
      const validCompanions = companions.filter(c => c.name.trim());
      if (validCompanions.length === 0) {
        toast.error(
          t('booking.confirmation.errors.companionRequired',
            'Has seleccionado "Con acompañantes" pero no has añadido ninguno. Rellena los datos del acompañante o selecciona "Solo para mí".'
          )
        );
        return;
      }
      const invalidCompanions = companions.filter(c => !c.name.trim());
      if (invalidCompanions.length > 0) {
        toast.error(
          t('booking.confirmation.errors.companionNameRequired',
            'Todos los acompañantes necesitan un nombre'
          )
        );
        return;
      }
    }

    if (!user) {
      toast.error(t('booking.confirmation.errors.notLoggedIn'));
      return;
    }

    if (!branch.branchId) {
      toast.error(t('booking.confirmation.errors.noBranch'));
      return;
    }

    setIsConfirming(true);
    const toastId = toast.loading(t('booking.confirmation.confirming'));

    try {
      let result;

      if (companions.length > 0) {
        // Create ticket with companions via Cloud Function
        result = await takeTicketWithCompanions({
          branchId: branch.branchId,
          mainTicket: {
            serviceId: service.serviceId,
            barberId: actualBarberId || 'any',
          },
          companions: companions.map(c => ({
            name: c.name.trim(),
            type: c.type,
            serviceId: c.serviceId || service.serviceId,
            barberId: c.barberId || 'any',
          })),
        });
      } else {
        // Create single ticket via Cloud Function
        result = await takeTicket({
          branchId: branch.branchId,
          serviceId: service.serviceId,
          // If no barber selected (null), send "any" to trigger auto-assignment
          barberId: actualBarberId || 'any',
        });
      }

      toast.dismiss(toastId);

      if (result.success) {
        // Remember last branch/service to pre-select them on the next visit
        try {
          localStorage.setItem(
            'booking-last-selection',
            JSON.stringify({ branchId: branch.branchId, serviceId: service.serviceId })
          );
        } catch {
          // localStorage unavailable (private mode, etc.) — ignore
        }

        const totalTickets = companions.length > 0 ? companions.length + 1 : 1;
        toast.success(t('booking.confirmation.success'), {
          description: totalTickets > 1
            ? t('booking.confirmation.ticketsCreated', { count: totalTickets })
            : `${t('booking.confirmation.ticketNumber')}: ${result.queueId.slice(-8).toUpperCase()}`,
        });

        // Navigate to client queue page
        setTimeout(() => {
          navigate('/client-queue');
        }, 1500);
      } else {
        toast.error(result.message || t('booking.confirmation.errors.failed'));
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(
        error instanceof Error
          ? error.message
          : t('booking.confirmation.errors.failed')
      );
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto pb-8">
      <div className="text-center mb-8">
        <h2 className="font-sans text-2xl font-semibold tracking-tight text-ink mb-1">
          {t('booking.confirmation.title')}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('booking.confirmation.subtitle')}
        </p>
      </div>

      {/* Summary Cards */}
      <BookingSummary
        service={service}
        barber={barber}
        isAnyBarber={isAnyBarber}
        preAssignedBarber={preAssignedBarber}
        loadingPreAssignment={loadingPreAssignment}
      />

      {/* Companion Selector */}
      <div className="mb-8">
        <CompanionSelector
          services={availableServices}
          barbers={availableBarbers}
          onCompanionsChange={setCompanions}
          onHasCompanionsChange={setHasCompanionsSelected}
          maxCompanions={5}
        />
      </div>

      {/* Companions Summary */}
      {companions.length > 0 && (
        <div className="bg-surface border border-hairline rounded-xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
            <h3 className="text-xs font-medium text-ink-subtle uppercase tracking-wide">
              {t('booking.confirmation.companionsSummary', 'Acompañantes')} ({companions.length})
            </h3>
          </div>
          <div className="space-y-3">
            {companions.map((companion, index) => {
              const companionService = availableServices.find(s => s.serviceId === companion.serviceId);
              const companionBarber = companion.barberId === 'any'
                ? null
                : availableBarbers.find(b => b.barberId === companion.barberId);

              return (
                <div key={index} className="flex items-center justify-between bg-surface-2 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-surface-3 flex items-center justify-center text-ink-subtle">
                      {companion.type === 'child'
                        ? <Baby className="w-5 h-5" aria-hidden="true" />
                        : <PersonStanding className="w-5 h-5" aria-hidden="true" />}
                    </div>
                    <div>
                      <p className="font-semibold text-ink">{companion.name}</p>
                      <p className="text-sm text-ink-muted">
                        {companionService?.name || service.name} •{' '}
                        {companionBarber ? companionBarber.displayName : t('booking.companions.anyBarber')}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-ink tabular-nums">
                    {((companionService?.price || service.price) / 100).toFixed(2)}€
                  </span>
                </div>
              );
            })}
            <div className="pt-3 border-t border-hairline flex items-center justify-between">
              <span className="text-sm text-ink-muted">
                {t('booking.confirmation.totalWithCompanions', 'Total estimado')}
              </span>
              <span className="text-xl font-semibold text-ink tabular-nums">
                {((service.price + companions.reduce((sum, c) => {
                  const cs = availableServices.find(s => s.serviceId === c.serviceId);
                  return sum + (cs?.price || service.price);
                }, 0)) / 100).toFixed(2)}€
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Queue Estimation - BIG FEATURE */}
      <QueueEstimationCard
        loading={loading}
        estimation={estimation}
        isOutsideWorkingHours={isOutsideWorkingHours}
        workingHoursWarning={workingHoursWarning}
      />

      {/* Important Note */}
      <div className="bg-surface-2 border border-hairline rounded-lg p-4 mb-8">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-ink-subtle shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-ink-muted leading-relaxed">
            {t('booking.confirmation.note')}
          </p>
        </div>
      </div>

      {/* Action Buttons - sticky bottom so Volver/Confirmar are always visible (same pattern as ClientQueue) */}
      <div className="sticky bottom-[calc(4rem+env(safe-area-inset-bottom,0px)+0.75rem)] lg:bottom-3 z-50 flex flex-col sm:flex-row gap-3 bg-canvas border border-hairline rounded-xl p-3 -m-3 shadow-sm">
        <Button
          onClick={onBack}
          variant="ghost"
          disabled={isConfirming}
          className="flex-1 h-12 sm:h-14 text-base sm:text-lg font-semibold"
        >
          <ArrowLeft className="w-5 h-5 mr-1" aria-hidden="true" /> {t('booking.buttons.back')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="primary"
          isLoading={isConfirming}
          disabled={isConfirming || loading}
          className="flex-1 h-12 sm:h-14 text-base sm:text-lg font-bold"
        >
          {isConfirming
            ? t('booking.confirmation.confirming')
            : t('booking.confirmation.confirmButton')}
        </Button>
      </div>

      {/* Out of Schedule Modal */}
      {overtimeData && (
        <OutOfScheduleModal
          isOpen={showOutOfScheduleModal}
          onClose={() => setShowOutOfScheduleModal(false)}
          barber={barberForEstimation}
          service={service}
          branch={branch}
          barberWorkEnd={overtimeData.barberWorkEnd}
          estimatedServiceEnd={overtimeData.estimatedServiceEnd}
          overtimeMinutes={overtimeData.overtimeMinutes}
          onSelectAlternativeBarber={handleSelectAlternativeBarber}
          onRequestOvertime={handleRequestOvertime}
          isRequestingOvertime={isRequestingOvertime}
        />
      )}

      {/* Guest Name Modal */}
      {firebaseUser && (
        <GuestNameModal
          isOpen={showGuestNameModal}
          userId={firebaseUser.uid}
          onClose={() => setShowGuestNameModal(false)}
          onNameSaved={() => {
            setGuestNameSaved(true);
            setShowGuestNameModal(false);
            // Auto-continue with booking after name saved
            setTimeout(() => {
              handleConfirm();
            }, 100);
          }}
        />
      )}
    </div>
  );
}
